import type { FileRecordDisplayKind } from "@cohesive/api-client";
import type {
  BlockStateEntry,
  BlockStateOp,
  BlockStateViewer,
} from "./htmlBlockRuntime";

/** What a block's runtime is seeded with. */
export interface BlockStateSnapshot {
  entries: readonly BlockStateEntry[];
  viewer: BlockStateViewer | null;
  readonly: boolean;
}

export type BlockStateSourceEvent =
  /** The whole state was (re)loaded; replaces what the block holds. */
  | { type: "reset"; snapshot: BlockStateSnapshot }
  /** Rows changed, already filtered to ones newer than the mirror.
   *  `writer` is what the submit that produced them passed, if any. */
  | { type: "change"; changes: readonly BlockStateEntry[]; writer?: object }
  /** The data could not be reached, or can again. */
  | { type: "unavailable"; unavailable: boolean };

/**
 * The host side of `cohesive.state` for one file: a mirror the block card
 * relays between the network and the frame. Surfaces that show the same
 * file share one source.
 */
export interface BlockStateSource {
  /** Null until the first load lands. */
  getSnapshot(): BlockStateSnapshot | null;
  /** Known before any load: baked into the document so `cohesive.viewer`
   *  is set when the block's scripts first run. */
  getBoot(): { viewer: BlockStateViewer | null; readonly: boolean };
  subscribe(listener: (event: BlockStateSourceEvent) => void): () => void;
  /** Resolves with every row the ops touched; rejects with a
   *  {@link BlockStateSourceError}. */
  submit(
    ops: readonly BlockStateOp[],
    writer?: object,
  ): Promise<BlockStateEntry[]>;
  /** The data cannot be reached: draw that rather than an empty block. */
  isUnavailable?(): boolean;
}

export class BlockStateSourceError extends Error {
  constructor(
    readonly code: string,
    message: string,
    /** On `rate_limited`: how long until a write is accepted again. */
    readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = "BlockStateSourceError";
  }
}

/** A frozen, read-only source: published views, where state is a snapshot
 *  taken at publish time and nobody is signed in. */
export function createStaticBlockStateSource(
  entries: readonly BlockStateEntry[],
): BlockStateSource {
  const snapshot: BlockStateSnapshot = {
    entries,
    viewer: null,
    readonly: true,
  };
  return {
    getSnapshot: () => snapshot,
    getBoot: () => ({ viewer: null, readonly: true }),
    subscribe: () => () => {},
    submit: () =>
      Promise.reject(
        new BlockStateSourceError(
          "readonly",
          "This is a published snapshot; its state is read-only.",
        ),
      ),
  };
}

/** The id of the JSON island a published document carries its state in. */
export const BLOCK_STATE_SNAPSHOT_ELEMENT_ID = "cohesive-state-snapshot";

/** Every block but a static one gets a state source; no kind is an app. */
export function blockWantsStateSource(
  kind: FileRecordDisplayKind | null | undefined,
): boolean {
  return kind !== "static";
}

/**
 * Bakes a published snapshot, in the server's wire shape, into a document as
 * the JSON island the runtime seeds itself from (read-only) — the client twin
 * of the publish-time bake.
 */
export function embedBlockStateSnapshot(
  html: string,
  state: { entries: readonly unknown[] } | undefined,
): string {
  if (!state || html.includes(BLOCK_STATE_SNAPSHOT_ELEMENT_ID)) return html;
  const json = JSON.stringify(state).replace(
    /[<>&\u2028\u2029]/g,
    (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`,
  );
  const island =
    `<script type="application/json" id="${BLOCK_STATE_SNAPSHOT_ELEMENT_ID}">${json}</` +
    `script>`;
  const head = html.search(/<\/head\s*>/i);
  if (head !== -1) return html.slice(0, head) + island + html.slice(head);
  const body = html.search(/<body[\s>]/i);
  if (body !== -1) return html.slice(0, body) + island + html.slice(body);
  return island + html;
}
